import * as fromActions from './actions';
import { TwoFactorChallenge, TwoFactorTranslationKey, UserModel } from '../../models';

export enum AuthenticatedState {
    Verifying = 1,
    Verified = 2,
}

export interface AuthState {
    userInfo: UserModel;
    isAuthenticated: AuthenticatedState;
    isAuthenticating: boolean;
    errorText: any;
    sessionMessageKey: fromActions.AuthSessionMessageKey;
    sessionMessageParams: fromActions.AuthSessionMessageParams;
    twoFactorChallenge: TwoFactorChallenge;
    twoFactorErrorKey: TwoFactorTranslationKey;
    isTwoFactorSubmitting: boolean;
    twoFactorResendAvailableAt: number;
}

const initialState: AuthState = {
    userInfo: null,
    isAuthenticated: null,
    isAuthenticating: null,
    errorText: null,
    sessionMessageKey: null,
    sessionMessageParams: null,
    twoFactorChallenge: null,
    twoFactorErrorKey: null,
    isTwoFactorSubmitting: false,
    twoFactorResendAvailableAt: 0,
};

export function authReducer(state = initialState, action: fromActions.AuthActions): AuthState {
    switch (action.type) {
        case fromActions.AuthActionTypes.CHECK_AUTH:
            return {
                ...state,
                isAuthenticating: true,
                isAuthenticated: AuthenticatedState.Verifying,
            };
        case fromActions.AuthActionTypes.CHECK_AUTH_ERROR:
            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: null,
            };
        case fromActions.AuthActionTypes.LOGIN_USER:
            return {
                ...state,
                isAuthenticating: true,
                errorText: null,
                sessionMessageKey: null,
                sessionMessageParams: null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        case fromActions.AuthActionTypes.LOGIN_USER_SUCCESS:
            const payloadSuccess = (action as fromActions.LoginUserSuccess).payload;

            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: AuthenticatedState.Verified,
                userInfo: payloadSuccess,
                errorText: null,
                sessionMessageKey: null,
                sessionMessageParams: null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        case fromActions.AuthActionTypes.LOGIN_USER_ERROR:
            const payloadError = (action as fromActions.LoginUserError).payload;

            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: null,
                userInfo: null,
                errorText: payloadError, // payloadError.error_description,
                sessionMessageKey: null,
                sessionMessageParams: null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        case fromActions.AuthActionTypes.LOGIN_USER_ERROR_CLEAR:
            const clearErrorAction = action as fromActions.LoginUserErrorClear;

            return {
                ...state,
                isAuthenticating: null,
                errorText: null,
                sessionMessageKey: clearErrorAction.clearSessionMessage
                    ? null
                    : state.sessionMessageKey,
                sessionMessageParams: clearErrorAction.clearSessionMessage
                    ? null
                    : state.sessionMessageParams,
            };
        case fromActions.AuthActionTypes.LOGIN_USER_TWO_FACTOR_REQUIRED:
            const twoFactorChallengeAction = action as fromActions.LoginUserTwoFactorRequired;

            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: null,
                userInfo: null,
                errorText: null,
                sessionMessageKey: null,
                sessionMessageParams: null,
                twoFactorChallenge: twoFactorChallengeAction.payload,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        case fromActions.AuthActionTypes.START_TWO_FACTOR_EMAIL_CODE:
        case fromActions.AuthActionTypes.RESEND_TWO_FACTOR_EMAIL_CODE:
        case fromActions.AuthActionTypes.VERIFY_TWO_FACTOR_EMAIL_CODE:
        case fromActions.AuthActionTypes.START_TWO_FACTOR_ID_GOV:
        case fromActions.AuthActionTypes.COMPLETE_TWO_FACTOR_ID_GOV:
            return {
                ...state,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: true,
            };
        case fromActions.AuthActionTypes.RESET_TWO_FACTOR_SUBMITTING:
            return {
                ...state,
                isTwoFactorSubmitting: false,
            };
        case fromActions.AuthActionTypes.START_TWO_FACTOR_EMAIL_CODE_SUCCESS:
            const emailCodeAction = action as fromActions.StartTwoFactorEmailCodeSuccess;

            return {
                ...state,
                twoFactorChallenge: state.twoFactorChallenge
                    ? {
                          ...state.twoFactorChallenge,
                          maskedEmail:
                              emailCodeAction.payload?.maskedEmail ||
                              state.twoFactorChallenge.maskedEmail,
                      }
                    : state.twoFactorChallenge,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: emailCodeAction.resendAvailableAt,
            };
        case fromActions.AuthActionTypes.TWO_FACTOR_ERROR:
            const twoFactorErrorAction = action as fromActions.TwoFactorError;

            return {
                ...state,
                twoFactorChallenge: twoFactorErrorAction.payload.clearChallenge
                    ? null
                    : state.twoFactorChallenge,
                twoFactorErrorKey: twoFactorErrorAction.payload.messageKey,
                isTwoFactorSubmitting: false,
            };
        case fromActions.AuthActionTypes.CANCEL_TWO_FACTOR:
            const cancelTwoFactorAction = action as fromActions.CancelTwoFactor;

            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: null,
                userInfo: null,
                errorText: null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
                sessionMessageKey: cancelTwoFactorAction.payload.messageKey ?? null,
                sessionMessageParams: cancelTwoFactorAction.payload.messageParams ?? null,
            };
        case fromActions.AuthActionTypes.LOGOUT_USER:
            const logoutPayload = (action as fromActions.LogoutUser).payload;

            return {
                ...state,
                isAuthenticated: null,
                userInfo: null,
                errorText: null,
                sessionMessageKey: logoutPayload.messageKey ?? null,
                sessionMessageParams: logoutPayload.messageParams ?? null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        case fromActions.AuthActionTypes.SET_AUTH_SESSION_MESSAGE:
            const sessionMessageAction = action as fromActions.SetAuthSessionMessage;

            return {
                ...state,
                isAuthenticating: null,
                isAuthenticated: null,
                userInfo: null,
                errorText: null,
                sessionMessageKey: sessionMessageAction.payload,
                sessionMessageParams: sessionMessageAction.params ?? null,
                twoFactorChallenge: null,
                twoFactorErrorKey: null,
                isTwoFactorSubmitting: false,
                twoFactorResendAvailableAt: 0,
            };
        default:
            return state;
    }
}
